import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../../helpers/methods";
import { getFormWebhookInfo } from "../../../services/metaLeadAdsService";

type FormWebhookData = {
  formId?: string;
  formName?: string;
  formType?: string;
  projectId?: string | null;
  organizationId?: string | null;
  ingestUrl?: string;
  method?: string;
  contentType?: string;
  auth?: string;
  verifyToken?: string;
  metaAppCallbackUrl?: string;
  fields?: { name: string; type?: string; required?: boolean }[];
  samplePayload?: Record<string, unknown>;
  sampleBatchPayload?: Record<string, unknown>[];
  acceptedFields?: {
    required?: string[];
    optional?: string[];
    batch?: string;
    note?: string;
  };
  curl?: { single?: string; batch?: string };
  note?: string;
};

/** Core flat sample when API has not returned field keys yet. */
function buildFlatSample(_formId: string, _formName?: string) {
  return {
    lead_name: "Jane Doe",
    lead_phone: "9876543210",
    lead_email: "jane@example.com",
    lead_source: "Facebook",
    project_name: "Any Project Name",
    remarks: "Anything Meta / Make sends — always stored as lead remark",
  } as Record<string, unknown>;
}

/** Prefer API sample when present (includes all form field keys). */
function normalizeSamplePayload(
  apiSample: Record<string, unknown> | undefined,
  formId: string,
  formName?: string,
  fields?: { name: string; type?: string; required?: boolean }[]
) {
  if (apiSample && typeof apiSample === "object" && !Array.isArray(apiSample)) {
    const cleaned: Record<string, unknown> = { ...apiSample };
    delete cleaned.dynamicFields;
    delete cleaned.form_id;
    delete cleaned.form_name;
    if (cleaned.message != null && cleaned.remarks == null) {
      cleaned.remarks = cleaned.message;
      delete cleaned.message;
    }
    if (!cleaned.lead_name) cleaned.lead_name = "Jane Doe";
    if (!cleaned.lead_phone) cleaned.lead_phone = "9876543210";
    if (!cleaned.lead_email) cleaned.lead_email = "jane@example.com";
    if (!cleaned.lead_source) cleaned.lead_source = "Facebook";
    if (cleaned.project_name == null) cleaned.project_name = "Any Project Name";
    if (!cleaned.remarks) {
      cleaned.remarks =
        "Anything Meta / Make sends — always stored as lead remark";
    }
    // Merge any missing form field labels as flat keys
    const skip = new Set(["section", "heading", "divider", "button", "html"]);
    (fields || []).forEach((f) => {
      const t = String(f.type || "").toLowerCase();
      if (skip.has(t)) return;
      const key = String(f.name || "").trim();
      if (!key || cleaned[key] != null) return;
      cleaned[key] = `Sample ${key}`;
    });
    return cleaned;
  }

  const base = buildFlatSample(formId, formName);
  const skip = new Set(["section", "heading", "divider", "button", "html"]);
  (fields || []).forEach((f) => {
    const t = String(f.type || "").toLowerCase();
    if (skip.has(t)) return;
    const key = String(f.name || "").trim();
    if (!key || key in base) return;
    base[key] = `Sample ${key}`;
  });
  return base;
}

const CopyBtn = ({ text, label = "Copy" }: { text: string; label?: string }) => (
  <button
    type="button"
    className="inline-flex h-7 shrink-0 items-center rounded-lg border border-[#E5E7EB] bg-white px-2.5 text-xs font-medium text-[#374151] hover:bg-[#F9FAFB]"
    onClick={async (e) => {
      e.stopPropagation();
      try {
        await navigator.clipboard.writeText(text);
        openSuccessNotification(`${label} copied`);
      } catch (_) {
        openErrorNotification("Unable to copy");
      }
    }}
  >
    Copy
  </button>
);

const DocRow = ({
  label,
  value,
  mono = true,
  copyText,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  copyText?: string;
}) => (
  <div className="flex flex-col gap-1 border-b border-[#F3F4F6] py-2.5 last:border-0 sm:flex-row sm:items-start sm:gap-4">
    <p className="w-36 shrink-0 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
      {label}
    </p>
    <div className="min-w-0 flex-1">
      <div className="flex items-start gap-2">
        <div
          className={`min-w-0 flex-1 break-all text-[12px] leading-relaxed text-[#111827] ${
            mono ? "font-mono" : ""
          }`}
        >
          {value}
        </div>
        {copyText ? <CopyBtn text={copyText} label={label} /> : null}
      </div>
    </div>
  </div>
);

type Props = {
  form: { _id: string; form_name?: string } | null;
  open: boolean;
  onClose: () => void;
};

/**
 * Popup: form-wise webhook URL + flat payload for Make / Meta / Zapier.
 */
const FormWebhookPopup = ({ form, open, onClose }: Props) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<FormWebhookData | null>(null);

  useEffect(() => {
    if (!open || !form?._id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setData(null);
      try {
        const res = await getFormWebhookInfo(String(form._id));
        if (!cancelled) {
          if (res?.success === 1 && res.data) setData(res.data);
          else {
            openErrorNotification(res?.message || "Failed to load webhook info");
          }
        }
      } catch (e: any) {
        if (!cancelled) {
          openErrorNotification(e?.message || "Failed to load webhook info");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, form?._id]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  const formId = String(form?._id || data?.formId || "");
  const formName = form?.form_name || data?.formName || "Form";
  const ingestUrl =
    data?.ingestUrl ||
    (formId ? `https://api.yhataw.com/meta/forms/${formId}/leads` : "");
  const method = data?.method || "POST";
  const contentType = data?.contentType || "application/json";

  const flatPayload = useMemo(
    () =>
      normalizeSamplePayload(
        data?.samplePayload,
        formId,
        formName,
        data?.fields
      ),
    [data?.samplePayload, data?.fields, formId, formName]
  );
  const payloadJson = JSON.stringify(flatPayload, null, 2);
  const batchJson = JSON.stringify(
    data?.sampleBatchPayload || [flatPayload, { ...flatPayload, lead_name: "John Smith", lead_phone: "9123456780", lead_source: "Instagram", remarks: "Call after 6pm" }],
    null,
    2
  );

  const requestSummary = useMemo(
    () =>
      [
        `URL: ${ingestUrl}`,
        `Method: ${method}`,
        `Body type: JSON (flat object or array of objects)`,
        `Content-Type: ${contentType}`,
        ``,
        `Request content (single lead):`,
        payloadJson,
        ``,
        `Request content (batch array):`,
        batchJson,
      ].join("\n"),
    [ingestUrl, method, contentType, payloadJson, batchJson]
  );

  if (!open || !form || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6"
      style={{ margin: 0 }}
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/45"
        aria-label="Close"
        onClick={onClose}
      />
      <div
        className="relative z-[10000] flex max-h-[min(90vh,860px)] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="form-webhook-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-[#F3F4F6] px-5 py-4">
          <div className="min-w-0">
            <h2
              id="form-webhook-title"
              className="truncate text-base font-semibold text-[#111827]"
            >
              Webhook — {formName}
            </h2>
            <p className="mt-0.5 text-xs text-[#6B7280]">
              Form-specific URL &amp; payload (no Meta account required)
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-2 py-1 text-sm text-[#6B7280] hover:bg-[#F3F4F6]"
          >
            Close
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {loading ? (
            <p className="text-sm text-[#9CA3AF]">Loading form webhook…</p>
          ) : null}

          {!loading ? (
            <>
              <div className="rounded-xl border border-[#E5E7EB] bg-[#F8FAFC] px-4 py-1">
                <DocRow
                  label="URL"
                  value={ingestUrl || "—"}
                  copyText={ingestUrl}
                />
                <DocRow label="Method" value={method} copyText={method} />
                <DocRow
                  label="Body type"
                  value="JSON — flat object, or array of flat objects (batch)"
                  mono={false}
                />
                <DocRow
                  label="Content-Type"
                  value={contentType}
                  copyText={contentType}
                />
                <DocRow
                  label="Auth"
                  value={data?.auth || "None (public form webhook)"}
                  mono={false}
                />
              </div>

              <div className="rounded-xl bg-[#FFF7ED] px-3 py-2.5 text-xs leading-relaxed text-[#9A3412]">
                Flat body only. Do <strong>not</strong> send nested{" "}
                <code>dynamicFields</code>. Use any{" "}
                <code>lead_source</code> / <code>project_name</code>. Always
                send <code>remarks</code> (stored as lead remark).
              </div>

              <div>
                <div className="mb-1.5 flex items-center justify-between gap-2">
                  <p className="text-xs font-semibold text-[#374151]">
                    Request content (single lead)
                  </p>
                  <CopyBtn text={payloadJson} label="Request body" />
                </div>
                <pre className="max-h-52 overflow-auto rounded-xl bg-[#0F172A] p-3 text-[11px] leading-relaxed text-[#E2E8F0]">
                  {payloadJson}
                </pre>
              </div>

              <div>
                <div className="mb-1.5 flex items-center justify-between gap-2">
                  <p className="text-xs font-semibold text-[#374151]">
                    Request content (batch array)
                  </p>
                  <CopyBtn text={batchJson} label="Batch body" />
                </div>
                <pre className="max-h-40 overflow-auto rounded-xl bg-[#0F172A] p-3 text-[11px] leading-relaxed text-[#E2E8F0]">
                  {batchJson}
                </pre>
              </div>

              <div>
                <div className="mb-1.5 flex items-center justify-between gap-2">
                  <p className="text-xs font-semibold text-[#374151]">
                    Full request summary (text)
                  </p>
                  <CopyBtn text={requestSummary} label="Summary" />
                </div>
                <pre className="max-h-48 overflow-auto rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] p-3 text-[11px] leading-relaxed text-[#111827]">
                  {requestSummary}
                </pre>
              </div>

              {formId ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-xl bg-[#F8FAFC] p-3">
                    <p className="text-xs font-semibold text-[#6B7280]">
                      Form ID
                    </p>
                    <div className="mt-1 flex items-center gap-2">
                      <code className="min-w-0 flex-1 break-all text-[11px]">
                        {formId}
                      </code>
                      <CopyBtn text={formId} label="Form ID" />
                    </div>
                  </div>
                  <div className="rounded-xl bg-[#F8FAFC] p-3">
                    <p className="text-xs font-semibold text-[#6B7280]">
                      Meta app callback (shared)
                    </p>
                    <div className="mt-1 flex items-start gap-2">
                      <code className="min-w-0 flex-1 break-all text-[11px]">
                        {data?.metaAppCallbackUrl ||
                          "https://api.yhataw.com/webhook"}
                      </code>
                      <CopyBtn
                        text={
                          data?.metaAppCallbackUrl ||
                          "https://api.yhataw.com/webhook"
                        }
                        label="Meta callback"
                      />
                    </div>
                    <p className="mt-1 break-all text-[11px] text-[#6B7280]">
                      Verify token:{" "}
                      <code>{data?.verifyToken || "facebook_webhook_123"}</code>
                    </p>
                  </div>
                </div>
              ) : null}

              <p className="text-xs text-[#6B7280]">
                {data?.note ||
                  "Push leads to this form-specific URL from Make.com / Zapier / Meta. CRM creates the lead on this Pulse form. No Meta OAuth required for this URL."}
              </p>
            </>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FormWebhookPopup;
